import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Container from "@/components/base/Container";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilmCard from "@/components/feature/FilmCard";
import FilterGroup from "@/pages/category/components/FilterGroup";
import { films } from "@/mocks/films";
import type { Film } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;
const SORTS = [
  { key: "time", label: "按更新时间" },
  { key: "hot", label: "按热门程度" },
];

function toList(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);

  const filmList = films as Film[];

  const types = toList(searchParams.get("type"));
  const genres = toList(searchParams.get("genre"));
  const regions = toList(searchParams.get("region"));
  const years = toList(searchParams.get("year"));
  const sort = searchParams.get("sort") || "time";
  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const title = types.length === 1 ? types[0] : "全部影视";
  useDocumentMeta(
    `${title}分类筛选－夜勤病栋影视分类`,
    "浏览并筛选夜勤病栋相关电影、电视剧的详细分类和筛选条件。"
  );

  const options = useMemo(() => {
    const genreSet = new Set<string>();
    const regionSet = new Set<string>();
    const yearSet = new Set<string>();
    filmList.forEach((film) => {
      film.genres.forEach((g) => genreSet.add(g));
      regionSet.add(film.region);
      yearSet.add(String(film.year));
    });
    return {
      types: ["电影", "电视剧"],
      genres: Array.from(genreSet),
      regions: Array.from(regionSet),
      years: Array.from(yearSet).sort((a, b) => Number(b) - Number(a)),
    };
  }, [filmList]);

  const filtered = useMemo(() => {
    const result = filmList.filter((film) => {
      const typeOk = types.length === 0 || types.includes(film.type);
      const genreOk = genres.length === 0 || film.genres.some((g) => genres.includes(g));
      const regionOk = regions.length === 0 || regions.includes(film.region);
      const yearOk = years.length === 0 || years.includes(String(film.year));
      return typeOk && genreOk && regionOk && yearOk;
    });
    return result.sort((a, b) => (sort === "hot" ? b.views - a.views : b.year - a.year));
  }, [filmList, types, genres, regions, years, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const toggleFilter = (key: string, value: string) => {
    const current = toList(searchParams.get(key));
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    updateParams({ [key]: next.length ? next.join(",") : null, page: null });
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setPanelOpen(false);
  };

  const hasFilters = types.length + genres.length + regions.length + years.length > 0;

  const filterContent = (
    <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
      <FilterGroup
        label="类型"
        options={options.types}
        selected={types}
        onToggle={(v) => toggleFilter("type", v)}
        onClear={() => updateParams({ type: null, page: null })}
      />
      <FilterGroup
        label="题材"
        options={options.genres}
        selected={genres}
        onToggle={(v) => toggleFilter("genre", v)}
        onClear={() => updateParams({ genre: null, page: null })}
      />
      <FilterGroup
        label="地区"
        options={options.regions}
        selected={regions}
        onToggle={(v) => toggleFilter("region", v)}
        onClear={() => updateParams({ region: null, page: null })}
      />
      <FilterGroup
        label="年份"
        options={options.years}
        selected={years}
        onToggle={(v) => toggleFilter("year", v)}
        onClear={() => updateParams({ year: null, page: null })}
      />
    </div>
  );

  return (
    <Container className="py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">影视分类</span>
        {types.length === 1 && (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{types[0]}</span>
          </>
        )}
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          夜勤病栋影视分类－{title}
        </h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
          浏览并筛选夜勤病栋相关电影、电视剧的详细分类和筛选条件。可按题材、地区、年份多选筛选，并按更新时间或热门程度排序。
        </p>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="hidden lg:block w-64 shrink-0">{filterContent}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-accent-600 font-semibold">{filtered.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="lg:hidden flex items-center gap-1.5 px-3 h-9 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line"></i>
                筛选
                {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>}
              </button>
              <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-md p-1">
                {SORTS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => updateParams({ sort: item.key, page: null })}
                    className={`px-3 h-8 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      sort === item.key
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {paged.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  onChange={(next) => {
                    updateParams({ page: next === 1 ? null : String(next) });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-search-eye-line"
              title="无匹配内容"
              description="当前筛选条件下没有找到相关影视作品，试试放宽一些条件吧。"
              actionLabel="重置筛选"
              onAction={resetAll}
            />
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="relative ml-auto w-[86%] max-w-sm h-full bg-background-100 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterContent}
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer"
              >
                查看 {filtered.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}